'use client'

/**
 * Presentational primitives shared by the scenarios 1–5 and the scenarios 6–7 drawers.
 *
 * They were defined inside `anomaly-case-drawer.tsx` and are moved here verbatim (same
 * markup, same classes) so the two drawers cannot drift apart: a section card, a labelled
 * fact row, a score tile and one collapsible block. Nothing here holds data or state
 * beyond the collapsible's own open flag.
 */
import { useState, type ReactNode } from 'react'
import { etsngRows } from '@/lib/etsng-classification'
import { cn } from '@/lib/utils'

/** Section card: bordered surface + small caps title. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4">
      <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]">{title}</h3>
      {children}
    </section>
  )
}

/** Label → value row. An absent value renders as «—»; callers that must hide empty rows filter first. */
export function Fact({ k, v, extra, title, dataAttr, help }: {
  k: string
  v: string | null | undefined
  extra?: string
  title?: string
  /** Опциональные `data-*` для строк, состояние которых проверяется тестом. */
  dataAttr?: Record<`data-${string}`, string>
  /** Вопросик рядом с подписью. Стоит в строке заголовка и высоты не добавляет. */
  help?: ReactNode
}) {
  return (
    <div className="grid grid-cols-[minmax(0,180px)_1fr] items-baseline gap-2" {...(dataAttr ?? {})}>
      <dt className="flex items-center gap-1.5 text-[11px] text-[var(--text-tertiary)]">
        <span>{k}</span>
        {help}
      </dt>
      <dd className="text-[12px] text-[var(--text-secondary)]" title={title}>
        {v ?? '—'}
        {extra && <span className="ml-1 text-[10px] text-[var(--text-tertiary)]">· {extra}</span>}
      </dd>
    </div>
  )
}

/**
 * One score tile: caption, big number, level word.
 *
 * `help` — необязательный элемент рядом с подписью (вопросик с пояснением).
 * Стоит В СТРОКЕ заголовка и высоты плитке не добавляет; без него разметка
 * плитки та же, что была.
 */
export function Metric({ label, value, accent, hint, help }: { label: string; value: string; accent?: string; hint?: string; help?: ReactNode }) {
  return (
    <div className="rounded-[3px] border border-[var(--border-subtle)] p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">
        <span>{label}</span>
        {help}
      </div>
      <div className={cn('mt-0.5 text-[16px] font-semibold tabular-nums', accent ?? 'text-[var(--text-primary)]')}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-[var(--text-secondary)]">{hint}</div>}
    </div>
  )
}

/** Collapsed-by-default block, styled like the scenarios 1–5 «Технические сведения». */
export function Collapsible({
  title,
  children,
  block,
}: {
  title: string
  children: ReactNode
  /** `data-block` value, so tests can address the section without matching its title. */
  block?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <section data-block={block} className="rounded-[4px] border border-[var(--border-subtle)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
      >
        {open ? '▾' : '▸'} {title}
      </button>
      {open && <div className="border-t border-[var(--border-subtle)] px-3 py-2">{children}</div>}
    </section>
  )
}

/**
 * Классификация груза по ЕТСНГ — справочные строки под грузом кейса.
 *
 * ЗАЧЕМ ОБЩИЙ КОМПОНЕНТ. Карточки сценариев 1–5 и 6–7 собирают факты по-разному, но
 * обе рисуют их одним и тем же `Fact`. Классификация здесь тоже обычные `Fact`-строки,
 * а не новая карточка: она читается как справка рядом с грузом и не должна спорить с
 * баллом и причинами. Единственная вольность — тонкий подзаголовок над парой строк,
 * иначе «Тарифная позиция» посреди перечня выглядит ещё одним свойством отправки.
 *
 * Подзаголовок печатается ТОЛЬКО когда блок общий для обеих ног. У разошедшихся кодов
 * строки стоят под своим грузом и подписаны стороной — общий заголовок там соврал бы.
 *
 * Компонент ничего не вычисляет: и коды, и наименования приходят готовыми из
 * `@/lib/etsng-classification`. Пустой классификации соответствует `null` — строк нет
 * вовсе, а не «— · —».
 */
export function EtsngClassificationRows({
  classification,
  heading,
  side,
}: {
  classification: import('@/lib/etsng-classification').EtsngClassification | null
  /** Подзаголовок над строками. Опущен — строки просто продолжают перечень. */
  heading?: string
  /** «импорта» / «экспорта» — подпись стороны, когда коды ног разошлись. */
  side?: string
}) {
  // Подписи и формат «код · наименование» приходят из общего контракта
  // `etsngRows` — здесь остаётся только разметка строки этой карточки.
  const rows = etsngRows(classification, side)
  if (rows.length === 0) return null
  return (
    <>
      {heading && (
        <div
          data-block="etsng-classification-heading"
          className="mt-2 text-[10px] font-medium uppercase tracking-[0.06em] text-[var(--text-tertiary)]"
        >
          {heading}
        </div>
      )}
      {rows.map((r) => (
        <Fact key={r.key} k={r.label} v={r.value} dataAttr={{ [`data-etsng-${r.key}`]: r.code }} />
      ))}
    </>
  )
}
